import { useState, useEffect } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";

const navLinks = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  {
    label: "Services",
    path: "/services",
    children: [
      { label: "CIO as a Service", path: "/services/cio" },
      { label: "IT Infrastructure", path: "/services/infrastructure" },
      { label: "ERP Operations", path: "/services/erp" },
      { label: "Cybersecurity & Compliance", path: "/services/cybersecurity" },
      { label: "AI Services", path: "/services/ai" },
    ],
  },
  { label: "Contact", path: "/contact" },
];

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navbar */}
      <nav
        data-testid="main-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/80 backdrop-blur-xl saturate-150 border-b border-white/40 shadow-sm"
            : "bg-white border-b border-gray-100"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" data-testid="logo-link" className="flex items-center">
              <img
                src="/teqxl-logo.jpeg"
                alt="TEQXL"
                className="h-16 lg:h-20 w-auto object-contain"
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) =>
                link.children ? (
                  <div
                    key={link.label}
                    className="relative group"
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                  >
                    <Link
                      to={link.path}
                      data-testid={`nav-${link.label.toLowerCase()}`}
                      className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors ${
                        isActive(link.path)
                          ? "text-[#002FA7]"
                          : "text-[#4B5563] hover:text-[#0A0A0A]"
                      }`}
                    >
                      {link.label}
                      <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" />
                    </Link>
                    {servicesOpen && (
                      <div className="absolute top-full left-0 pt-1 animate-fade-in">
                        <div className="bg-white border border-gray-200 shadow-lg min-w-[220px]">
                          {link.children.map((child) => (
                            <Link
                              key={child.path}
                              to={child.path}
                              data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, "-")}`}
                              className="flex items-center justify-between px-5 py-3 text-sm text-[#4B5563] hover:text-[#002FA7] hover:bg-gray-50 transition-colors"
                            >
                              {child.label}
                              <ArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100" />
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.label}
                    to={link.path}
                    data-testid={`nav-${link.label.toLowerCase()}`}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      isActive(link.path)
                        ? "text-[#002FA7]"
                        : "text-[#4B5563] hover:text-[#0A0A0A]"
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              )}
            </div>

            {/* Mobile Toggle */}
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 text-[#0A0A0A]"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden border-t border-gray-100 bg-white animate-fade-in"
          >
            <div className="px-6 py-4 space-y-1">
              {navLinks.map((link) =>
                link.children ? (
                  <div key={link.label}>
                    <button
                      onClick={() => setServicesOpen(!servicesOpen)}
                      className="flex items-center justify-between w-full px-3 py-3 text-sm font-medium text-[#4B5563]"
                    >
                      {link.label}
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    {servicesOpen && (
                      <div className="pl-4 space-y-1">
                        {link.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            data-testid={`mobile-nav-${child.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="block px-3 py-2 text-sm text-[#4B5563] hover:text-[#002FA7]"
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.label}
                    to={link.path}
                    data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                    className="block px-3 py-3 text-sm font-medium text-[#4B5563] hover:text-[#002FA7]"
                  >
                    {link.label}
                  </Link>
                )
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="flex-1 pt-16 lg:pt-20">
        <Outlet />
      </main>

      {/* Footer */}
      <footer data-testid="main-footer" className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
            {/* Brand */}
            <div className="lg:col-span-1">
              <Link to="/" className="inline-block mb-6">
                <span className="font-heading text-2xl font-bold tracking-tighter">
                  <span className="text-[#002FA7]">TEQ</span>
                  <span className="text-white">XL</span>
                </span>
              </Link>
              <p className="text-sm text-gray-400 leading-relaxed max-w-xs">
                Turning Technology into Business Value. Your strategic technology partner
                for enterprise-scale digital transformation.
              </p>
            </div>

            {/* Services */}
            <div>
              <h4 className="font-heading text-xs tracking-[0.2em] uppercase font-bold text-gray-400 mb-6">
                Services
              </h4>
              <div className="space-y-3">
                <Link to="/services/cio" className="block text-sm text-gray-300 hover:text-white transition-colors">
                  CIO as a Service
                </Link>
                <Link to="/services/infrastructure" className="block text-sm text-gray-300 hover:text-white transition-colors">
                  IT Infrastructure
                </Link>
                <Link to="/services/erp" className="block text-sm text-gray-300 hover:text-white transition-colors">
                  ERP Operations
                </Link>
                <Link to="/services/cybersecurity" className="block text-sm text-gray-300 hover:text-white transition-colors">
                  Cybersecurity & Compliance
                </Link>
                <Link to="/services/ai" className="block text-sm text-gray-300 hover:text-white transition-colors">
                  AI Services
                </Link>
              </div>
            </div>

            {/* Company */}
            <div>
              <h4 className="font-heading text-xs tracking-[0.2em] uppercase font-bold text-gray-400 mb-6">
                Company
              </h4>
              <div className="space-y-3">
                <Link to="/about" className="block text-sm text-gray-300 hover:text-white transition-colors">
                  About Us
                </Link>
                <Link to="/about" className="block text-sm text-gray-300 hover:text-white transition-colors">
                  Our Mission
                </Link>
                <Link to="/contact" className="block text-sm text-gray-300 hover:text-white transition-colors">
                  Contact
                </Link>
              </div>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-heading text-xs tracking-[0.2em] uppercase font-bold text-gray-400 mb-6">
                Get in Touch
              </h4>
              <div className="space-y-3">
                <p className="text-sm text-gray-300">info@teqxl.com</p>
                <Link
                  to="/contact"
                  data-testid="footer-contact-link"
                  className="inline-flex items-center gap-2 text-sm text-[#002FA7] hover:text-blue-400 transition-colors"
                >
                  Contact Us <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-16 pt-8 border-t border-gray-800 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-xs text-gray-500">
              &copy; {new Date().getFullYear()} TEQXL Technology Consultancy LLP. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <span className="text-xs text-gray-500 hover:text-gray-400 cursor-pointer">Privacy Policy</span>
              <span className="text-xs text-gray-500 hover:text-gray-400 cursor-pointer">Terms of Service</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
